import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { restoreChatTag } from '../../../hooks/chat/api';
import { ChatTag } from '../tagGlossary';
import { LABEL_MAX } from './TagDialog';

interface Props {
  open: boolean;
  tag: ChatTag | null;
  onClose: () => void;
  onRestored: () => void;
  onError: (error: unknown) => void;
}

/**
 * Brings a retired tag back, under a new label when its old one now
 * collides with an active tag of the category (ant refuses that restore
 * otherwise). The label travels in the same write as the restore.
 */
export function RestoreTagDialog({ open, tag, onClose, onRestored, onError }: Props) {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setLabel(tag?.label ?? '');
  }, [open, tag]);

  const restore = async () => {
    if (!tag || !label.trim()) return;
    setSaving(true);
    try {
      const renamed = label.trim() !== tag.label;
      await restoreChatTag(tag.id, renamed ? { label: label.trim() } : undefined);
      onRestored();
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Restore tag</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The tag is offered again. Change the label if an active tag of the category
          already uses it.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          inputProps={{ maxLength: LABEL_MAX, 'aria-label': 'Restored label' }}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={restore}
          disabled={saving || !label.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}
